import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = ({ onLoginSuccess, onBack }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCreatingFirstAdmin, setIsCreatingFirstAdmin] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        username,
        password
      });

      // Store admin token
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_id', response.data.user_id);
      localStorage.setItem('admin_username', response.data.username);

      onLoginSuccess();
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid admin credentials');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFirstAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/admin/create-first-admin`, {
        username,
        password
      });

      // Now login with these credentials
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        username,
        password
      });

      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_id', response.data.user_id);
      localStorage.setItem('admin_username', response.data.username);

      onLoginSuccess();
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Admin account already exists. Please login instead.');
        setIsCreatingFirstAdmin(false);
      } else {
        setError('Failed to create admin account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {onBack && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4 text-white hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        )}

        <Card className="border-slate-700 bg-white/95 backdrop-blur-sm shadow-2xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">
              {isCreatingFirstAdmin ? 'Create Admin Account' : 'Admin Login'}
            </CardTitle>
            <CardDescription>
              {isCreatingFirstAdmin
                ? 'Create the first admin account to manage Speaker Notes'
                : 'Access the Speaker Notes admin panel'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={isCreatingFirstAdmin ? handleCreateFirstAdmin : handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="admin-username">Username</Label>
                <Input
                  id="admin-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Admin username"
                  required
                  autoComplete="username"
                />
              </div>
              <div>
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Admin password"
                  required
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600" disabled={loading}>
                {loading ? 'Please wait...' : isCreatingFirstAdmin ? 'Create Admin' : 'Login'}
              </Button>
            </form>

            {/* Create first admin link removed - admin already exists */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
